import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { getShipmentsBySeller } from '../api/client';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, ExternalLink } from 'lucide-react';
import type { Shipment, ShipmentStatus } from '../types';

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

function getTrackingUrl(carrier: string, trackingNumber: string): string | null {
  switch (carrier) {
    case 'USPS': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    case 'UPS': return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    case 'FedEx': return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    default: return null;
  }
}

const STATUS_CONFIG: Record<ShipmentStatus, { label: string; color: string; bgColor: string }> = {
  CREATED: { label: 'Created', color: '#F59E0B', bgColor: '#FEF3C7' },
  LABEL_PURCHASED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE' },
  IN_TRANSIT: { label: 'In Transit', color: '#007AFF', bgColor: '#DBEAFE' },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  EXCEPTION: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

type FilterTab = 'all' | ShipmentStatus;

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'CREATED', label: 'Created' },
  { id: 'LABEL_PURCHASED', label: 'Label Created' },
  { id: 'IN_TRANSIT', label: 'In Transit' },
  { id: 'DELIVERED', label: 'Delivered' },
  { id: 'EXCEPTION', label: 'Exception' },
];

const GRID_COLUMNS = '1fr 1fr 1fr 0.7fr 1.5fr 0.8fr';

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SellerShipmentsPage() {
  useDocumentTitle('Shipments');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  useEffect(() => {
    loadShipments();
  }, [page, activeFilter]);

  const loadShipments = async () => {
    try {
      setIsLoading(true);
      const status = activeFilter === 'all' ? undefined : activeFilter;
      const data = await getShipmentsBySeller(page, 20, status);
      setShipments(data.content);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shipments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filter: FilterTab) => {
    setActiveFilter(filter);
    setPage(0);
  };

  return (
    <SellerLayout>
      <h2 style={styles.pageHeading}>Shipments</h2>

      {/* Filter tabs */}
      <div style={styles.filterTabs}>
        {FILTER_TABS.map(tab => (
          <button
            key={tab.id}
            style={{
              ...styles.filterTab,
              ...(activeFilter === tab.id ? styles.filterTabActive : {}),
            }}
            onClick={() => handleFilterChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {isLoading ? (
        <div style={styles.loading}>Loading shipments...</div>
      ) : shipments.length === 0 ? (
        <div style={styles.emptyState}>
          <Package size={48} color={colors.gray[300]} />
          <p style={styles.emptyText}>No shipments found</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: isMobile ? 'auto' : undefined, WebkitOverflowScrolling: 'touch' }}>
          <div style={{ ...styles.table, minWidth: isMobile ? '800px' : undefined }}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: GRID_COLUMNS }}>
              <span>Shipment ID</span>
              <span>Order ID</span>
              <span>Status</span>
              <span>Items</span>
              <span>Tracking</span>
              <span style={{ textAlign: 'right' }}>Created</span>
            </div>
            {shipments.map(shipment => {
              const sc = STATUS_CONFIG[shipment.status];
              return (
                <div
                  key={shipment.id}
                  style={{ ...styles.tableRow, gridTemplateColumns: GRID_COLUMNS }}
                  onClick={() => navigate(`/sell/shipments/${shipment.id}`)}
                >
                  <span style={styles.idCell}>{shipment.id}</span>
                  <span style={styles.idCell}>{shipment.orderId}</span>
                  <span>
                    <span style={{ ...styles.statusBadge, backgroundColor: sc.bgColor, color: sc.color }}>
                      {sc.label}
                    </span>
                  </span>
                  <span style={styles.tableCell}>
                    {shipment.itemSaleCount} item{shipment.itemSaleCount !== 1 ? 's' : ''}
                  </span>
                  <span>
                    {shipment.carrier && shipment.trackingNumber ? (
                      <span style={styles.trackingColumn}>
                        <span style={styles.trackingCarrierRow}>
                          {CARRIER_LOGOS[shipment.carrier] && (
                            <img src={CARRIER_LOGOS[shipment.carrier]} alt={shipment.carrier} style={styles.carrierLogoInline} />
                          )}
                          <span>{shipment.carrier}</span>
                        </span>
                        {(() => {
                          const url = getTrackingUrl(shipment.carrier!, shipment.trackingNumber!);
                          return url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.trackingLink}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {shipment.trackingNumber}
                              <ExternalLink size={12} />
                            </a>
                          ) : (
                            <span style={styles.trackingNumberText}>{shipment.trackingNumber}</span>
                          );
                        })()}
                      </span>
                    ) : (
                      <span style={styles.noTracking}>-</span>
                    )}
                  </span>
                  <span style={{ ...styles.tableCell, textAlign: 'right', color: colors.gray[600] }}>
                    {formatDate(shipment.createdAt)}
                  </span>
                </div>
              );
            })}
          </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.paginationButton, ...(page === 0 ? styles.paginationDisabled : {}) }}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>Page {page + 1} of {totalPages}</span>
              <button
                style={{ ...styles.paginationButton, ...(page >= totalPages - 1 ? styles.paginationDisabled : {}) }}
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeading: {
    fontSize: '20px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 16px 0',
  },
  filterTabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  filterTab: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  filterTabActive: {
    backgroundColor: colors.brand.blue,
    color: '#FFFFFF',
    fontWeight: 600,
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    padding: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '64px 0',
  },
  emptyText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  table: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'grid',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  tableCell: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  idCell: {
    fontSize: '13px',
    fontFamily: 'monospace',
    color: colors.brand.blue,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '20px',
  },
  paginationButton: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.brand.blue,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    cursor: 'pointer',
  },
  paginationDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  pageInfo: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  trackingColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  trackingCarrierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[700],
  },
  carrierLogoInline: {
    height: '14px',
    width: 'auto',
    objectFit: 'contain',
  },
  trackingLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: colors.brand.blue,
    textDecoration: 'none',
    fontFamily: 'monospace',
  },
  trackingNumberText: {
    fontSize: '12px',
    color: colors.gray[600],
    fontFamily: 'monospace',
  },
  noTracking: {
    color: colors.gray[400],
  },
};
